import React, { useState } from 'react';
import { useNavigation } from '@react-navigation/native';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Modal,
  ScrollView,
  StyleSheet,
  Alert,
  Dimensions,
} from 'react-native';
import { useAuth } from '../context/AuthContext';
import APIService, { ClienteRegisterForm, Marca, Modelo, Zona } from '../services/api';
import DepartmentSelector, { Departamento, Municipio } from '../components/DepartmentSelector';
import MarcaModeloSelector from '../components/MarcaModeloSelector';
import ClienteSelector from '../components/ClienteSelector';
import ZonaSelector from '../components/ZonaSelector';

const { height: screenHeight } = Dimensions.get('window');

const RegisterScreen: React.FC = () => {
  const navigation = useNavigation();
  const { register } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [checkingPhone, setCheckingPhone] = useState(false);
  
  // Estados para validación visual
  const [errors, setErrors] = useState<{[key: string]: boolean}>({});
  const [errorMessages, setErrorMessages] = useState<{[key: string]: string}>({});
  
  // Refs para los campos de texto
  const nombreRef = React.useRef<any>(null);
  const correoRef = React.useRef<any>(null);
  const telefonoRef = React.useRef<any>(null);
  const passwordRef = React.useRef<any>(null);
  const passwordConfirmRef = React.useRef<any>(null);
  
  // Estados para los objetos completos de departamento y municipio
  const [selectedDepartamento, setSelectedDepartamento] = useState<Departamento | undefined>();
  const [selectedMunicipio, setSelectedMunicipio] = useState<Municipio | undefined>();
  
  // Estados para los objetos completos de marca y modelo
  const [selectedMarca, setSelectedMarca] = useState<Marca | undefined>();
  const [selectedModelo, setSelectedModelo] = useState<Modelo | undefined>();
  
  // Estado para el cliente referidor
  const [selectedReferido, setSelectedReferido] = useState<{ id: number; nombre: string; telefono: string } | null>(null);
  
  // Estado para la zona seleccionada
  const [selectedZona, setSelectedZona] = useState<Zona | undefined>();
  
  // Estados para cumpleaños
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [showDayPicker, setShowDayPicker] = useState(false);
  
  // Datos de meses
  const meses = [
    { id: 1, nombre: 'Enero' },
    { id: 2, nombre: 'Febrero' },
    { id: 3, nombre: 'Marzo' },
    { id: 4, nombre: 'Abril' },
    { id: 5, nombre: 'Mayo' },
    { id: 6, nombre: 'Junio' },
    { id: 7, nombre: 'Julio' },
    { id: 8, nombre: 'Agosto' },
    { id: 9, nombre: 'Septiembre' },
    { id: 10, nombre: 'Octubre' },
    { id: 11, nombre: 'Noviembre' },
    { id: 12, nombre: 'Diciembre' },
  ];
  
  // Calcular días según el mes seleccionado
  const getDaysInMonth = (month?: number): number => {
    if (!month) return 31;
    const diasPorMes = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return diasPorMes[month - 1];
  };
  
  const [formData, setFormData] = useState<ClienteRegisterForm>({
    nombre: '',
    telefono: '',
    correo: '',
    marca: undefined,
    modelo: undefined,
    referido_por: undefined,
    departamento: undefined,
    municipio: undefined,
    zona: undefined,
    linea: undefined,
    nit: '',
    dia_cumple: undefined,
    mes_cumple: undefined,
    password: '',
    password_confirmation: '',
  });

  const navigateToLogin = () => {
    (navigation as any).navigate('Login');
  };

  // Verificar si el correo ya existe
  const checkEmailExists = async (email: string): Promise<boolean> => {
    try {
      return await APIService.checkEmailExists(email);
    } catch (error) {
      console.error('Error verificando correo:', error);
      return false;
    }
  };

  // Verificar si el teléfono ya existe
  const checkPhoneExists = async (phone: string): Promise<boolean> => {
    try {
      return await APIService.checkPhoneExists(phone);
    } catch (error) {
      console.error('Error verificando teléfono:', error);
      return false;
    }
  };

  const handleRegister = async () => {
    console.log('🚀 RegisterScreen: handleRegister called');
    console.log('📋 FormData completo:', formData);
    console.log('📧 Correo value:', `"${formData.correo}"`, 'Length:', formData.correo?.length || 0);
    
    // Limpiar errores previos
    setErrors({});
    setErrorMessages({});
    
    // Validaciones básicas con marcado visual
    if (!formData.nombre.trim()) {
      console.log('❌ Validación falló: nombre vacío');
      setErrors({ nombre: true });
      setErrorMessages({ nombre: 'El nombre es obligatorio' });
      nombreRef.current?.focus();
      Alert.alert('Error', 'Por favor ingresa tu nombre completo');
      return;
    }
    if (!formData.correo || !formData.correo.trim()) {
      console.log('❌ Validación falló: correo vacío');
      setErrors({ correo: true });
      setErrorMessages({ correo: 'El correo es obligatorio' });
      correoRef.current?.focus();
      Alert.alert('Error', 'Por favor ingresa tu correo electrónico');
      return;
    }
    // Validar formato de correo
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.correo.trim())) {
      console.log('❌ Validación falló: formato de correo inválido');
      setErrors({ correo: true });
      setErrorMessages({ correo: 'Formato de correo inválido' });
      correoRef.current?.focus();
      Alert.alert('Error', 'Por favor ingresa un correo electrónico válido');
      return;
    }
    
    // Verificar si el correo ya existe ANTES de continuar
    console.log('📧 Verificando si el correo ya existe...');
    setCheckingEmail(true);
    const emailExists = await checkEmailExists(formData.correo.trim());
    setCheckingEmail(false);
    
    if (emailExists) {
      console.log('❌ Validación falló: correo ya existe');
      setErrors({ correo: true });
      setErrorMessages({ correo: 'Este correo ya está registrado' });
      correoRef.current?.focus();
      Alert.alert(
        'Correo Ya Registrado',
        'Este correo electrónico ya tiene una cuenta. Por favor usa otro correo o inicia sesión.',
        [
          { text: 'Usar otro correo', style: 'cancel' },
          { text: 'Ir al Login', onPress: navigateToLogin }
        ]
      );
      return;
    }
    if (!formData.telefono.trim()) {
      console.log('❌ Validación falló: teléfono vacío');
      setErrors({ telefono: true });
      setErrorMessages({ telefono: 'El número de teléfono es obligatorio' });
      telefonoRef.current?.focus();
      Alert.alert('Error', 'Por favor ingresa tu número de WhatsApp');
      return;
    }
    
    // Verificar si el teléfono ya existe ANTES de continuar
    console.log('📱 Verificando si el teléfono ya existe...');
    setCheckingPhone(true);
    const phoneExists = await checkPhoneExists(formData.telefono.trim());
    setCheckingPhone(false);
    
    if (phoneExists) {
      console.log('❌ Validación falló: teléfono ya existe');
      setErrors({ telefono: true });
      setErrorMessages({ telefono: 'Este teléfono ya está registrado' });
      telefonoRef.current?.focus();
      Alert.alert(
        'Teléfono Ya Registrado',
        'Este número de teléfono ya tiene una cuenta. Por favor usa otro número o inicia sesión.',
        [
          { text: 'Usar otro número', style: 'cancel' },
          { text: 'Ir al Login', onPress: navigateToLogin }
        ]
      );
      return;
    }
    
    if (formData.password.length < 6) {
      console.log('❌ Validación falló: contraseña muy corta');
      setErrors({ password: true });
      setErrorMessages({ password: 'Mínimo 6 caracteres' });
      passwordRef.current?.focus();
      Alert.alert('Error', 'La contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (formData.password !== formData.password_confirmation) {
      console.log('❌ Validación falló: contraseñas no coinciden');
      setErrors({ password: true, password_confirmation: true });
      setErrorMessages({ password_confirmation: 'Las contraseñas no coinciden' });
      passwordConfirmRef.current?.focus();
      Alert.alert('Error', 'Las contraseñas no coinciden');
      return;
    }
    
    console.log('✅ Todas las validaciones pasaron, procediendo con registro...');

    setIsLoading(true);
    
    try {
      await register(formData);
      
      // Mostrar mensaje de éxito y redirigir al login
      Alert.alert(
        '¡Cuenta creada exitosamente!', 
        'Tu cuenta ha sido creada con ID empresa 30. Por favor inicia sesión con tus credenciales.',
        [
          {
            text: 'Ir al Login',
            onPress: () => {
              setModalVisible(false);
              navigateToLogin(); // Redirigir al login
            }
          }
        ]
      );
    } catch (error: any) {
      console.error('Registration error:', error);
      
      // Analizar el mensaje de error para proporcionar feedback específico
      const errorMessage = error.message || 'Error al crear la cuenta';
      
      // Verificar si el error es de teléfono duplicado
      if (errorMessage.includes('telefono') || errorMessage.includes('The telefono has already been taken')) {
        setErrors({ telefono: true });
        setErrorMessages({ telefono: 'Este teléfono ya está registrado' });
        telefonoRef.current?.focus();
        Alert.alert(
          'Teléfono Ya Registrado',
          'Este número de teléfono ya está en uso. Por favor usa otro número.',
          [{ text: 'OK' }]
        );
      }
      // Verificar si el error es de correo duplicado
      else if (errorMessage.includes('correo') || errorMessage.includes('email') || errorMessage.includes('The correo has already been taken')) {
        setErrors({ correo: true });
        setErrorMessages({ correo: 'Este correo ya está registrado' });
        correoRef.current?.focus();
        Alert.alert(
          'Correo Ya Registrado',
          'Este correo electrónico ya está en uso. Por favor usa otro correo.',
          [{ text: 'OK' }]
        );
      }
      // Error genérico
      else {
        Alert.alert('Error', errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <View style={styles.container}>
      {/* Pantalla principal con botón para abrir modal */}
      <View style={styles.welcomeContainer}>
        <Text style={styles.welcomeTitle}>Bienvenido a MotoMarketGT</Text>
        <Text style={styles.welcomeSubtitle}>Crea tu cuenta para comenzar</Text>
        
        <TouchableOpacity 
          style={styles.openModalButton}
          onPress={() => setModalVisible(true)}
        >
          <Text style={styles.openModalButtonText}>✨ CREAR MI CUENTA</Text>
        </TouchableOpacity>
        
        <TouchableOpacity style={styles.loginLink} onPress={navigateToLogin}>
          <Text style={styles.loginLinkText}>¿Ya tienes cuenta? Inicia sesión</Text>
        </TouchableOpacity>
      </View>

      {/* Modal con formulario completo */}
      <Modal
        visible={modalVisible}
        animationType="slide"
        presentationStyle="pageSheet"
      >
        <View style={styles.modalContainer}>
          {/* Header del modal */}
          <View style={styles.modalHeader}>
            <TouchableOpacity 
              style={styles.closeButton}
              onPress={() => setModalVisible(false)}
            >
              <Text style={styles.closeButtonText}>✕</Text>
            </TouchableOpacity>
            <Text style={styles.modalTitle}>Crear Cuenta</Text>
            <View style={styles.placeholder} />
          </View>

          {/* Formulario con scroll que SÍ funciona en modal */}
          <ScrollView style={styles.modalScrollView} showsVerticalScrollIndicator={true}>
            <View style={styles.formContainer}>
              {/* Nombre */}
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Nombre completo *</Text>
                <TextInput
                  ref={nombreRef}
                  style={[styles.input, errors.nombre && styles.inputError]}
                  value={formData.nombre}
                  onChangeText={(value) => {
                    setFormData(prev => ({ ...prev, nombre: value }));
                    if (errors.nombre) {
                      setErrors(prev => ({ ...prev, nombre: false }));
                      setErrorMessages(prev => ({ ...prev, nombre: '' }));
                    }
                  }}
                  placeholder="Tu nombre completo"
                  autoCapitalize="words"
                />
                {errorMessages.nombre && (
                  <Text style={styles.errorText}>{errorMessages.nombre}</Text>
                )}
              </View>
              
              {/* WhatsApp */}
              <View style={styles.inputGroup}>
                <Text style={styles.label}>WhatsApp *</Text>
                <TextInput
                  ref={telefonoRef}
                  style={[styles.input, errors.telefono && styles.inputError]}
                  value={formData.telefono}
                  onChangeText={(value) => {
                    setFormData(prev => ({ ...prev, telefono: value }));
                    if (errors.telefono) {
                      setErrors(prev => ({ ...prev, telefono: false }));
                      setErrorMessages(prev => ({ ...prev, telefono: '' }));
                    }
                  }}
                  placeholder="5551234567"
                  keyboardType="phone-pad"
                  editable={!checkingPhone}
                />
                {checkingPhone && (
                  <Text style={styles.infoText}>Verificando teléfono...</Text>
                )}
                {errorMessages.telefono && (
                  <Text style={styles.errorText}>{errorMessages.telefono}</Text>
                )}
              </View>
              
              {/* Correo */}
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Correo electrónico *</Text>
                <TextInput
                  ref={correoRef}
                  style={[styles.input, errors.correo && styles.inputError]}
                  value={formData.correo}
                  onChangeText={(value) => {
                    setFormData(prev => ({ ...prev, correo: value }));
                    if (errors.correo) {
                      setErrors(prev => ({ ...prev, correo: false }));
                      setErrorMessages(prev => ({ ...prev, correo: '' }));
                    }
                  }}
                  placeholder="tu@correo.com"
                  keyboardType="email-address"
                  autoCapitalize="none"
                  editable={!checkingEmail}
                />
                {checkingEmail && (
                  <Text style={styles.infoText}>Verificando correo...</Text>
                )}
                {errorMessages.correo && (
                  <Text style={styles.errorText}>{errorMessages.correo}</Text>
                )}
              </View>
              
              {/* Marca y Modelo - Selector funcionando */}
              <MarcaModeloSelector
                selectedMarca={selectedMarca}
                selectedModelo={selectedModelo}
                onMarcaChange={(marca: Marca) => {
                  console.log('🏍️ RegisterScreen: Marca seleccionada:', marca);
                  setSelectedMarca(marca);
                  setSelectedModelo(undefined); // Limpiar modelo al cambiar marca
                  setFormData(prev => ({
                    ...prev,
                    marca: marca,
                    modelo: undefined
                  }));
                }}
                onModeloChange={(modelo: Modelo) => {
                  console.log('🏍️ RegisterScreen: Modelo seleccionado:', modelo);
                  setSelectedModelo(modelo);
                  setFormData(prev => ({
                    ...prev,
                    modelo: modelo
                  }));
                }}
              />

              {/* Referido por - ClienteSelector */}
              <ClienteSelector
                selectedCliente={selectedReferido}
                onClienteChange={(cliente) => {
                  console.log('👥 RegisterScreen: Cliente referidor seleccionado:', cliente);
                  setSelectedReferido(cliente);
                  setFormData(prev => ({
                    ...prev,
                    referido_por: cliente?.id
                  }));
                }}
              />

              {/* Departamento - Selector funcionando */}
              <View style={styles.inputGroup}>
                <DepartmentSelector
                  selectedDepartamento={selectedDepartamento}
                  selectedMunicipio={selectedMunicipio}
                  onDepartamentoChange={(departamento: Departamento) => {
                    console.log('🏛️ RegisterScreen: Departamento seleccionado:', departamento);
                    setSelectedDepartamento(departamento);
                    setSelectedMunicipio(undefined); // Limpiar municipio al cambiar departamento
                    setSelectedZona(undefined); // Limpiar zona al cambiar departamento
                    setFormData(prev => ({
                      ...prev,
                      departamento: departamento,
                      municipio: undefined,
                      zona: undefined
                    }));
                  }}
                  onMunicipioChange={(municipio: Municipio) => {
                    console.log('🏛️ RegisterScreen: Municipio seleccionado:', municipio);
                    setSelectedMunicipio(municipio);
                    setSelectedZona(undefined); // Limpiar zona al cambiar municipio
                    setFormData(prev => ({
                      ...prev,
                      municipio: municipio,
                      zona: undefined
                    }));
                  }}
                />
              </View>

              {/* Zona - Selector dinámico */}
              <ZonaSelector
                municipioId={selectedMunicipio?.id}
                selectedZona={selectedZona}
                onZonaChange={(zona: Zona) => {
                  console.log('📍 RegisterScreen: Zona seleccionada:', zona);
                  setSelectedZona(zona);
                  setFormData(prev => ({
                    ...prev,
                    zona: zona.id
                  }));
                }}
              />
              
              {/* NIT */}
              <View style={styles.inputGroup}>
                <Text style={styles.label}>NIT</Text>
                <TextInput
                  style={styles.input}
                  value={formData.nit}
                  onChangeText={(value) => setFormData(prev => ({ ...prev, nit: value }))}
                  placeholder="NIT"
                />
              </View>
              
              {/* Cumpleaños */}
              <View style={styles.inputGroup}>
                <Text style={styles.label}>📅 Fecha de Cumpleaños (Opcional)</Text>
                <View style={styles.birthdayContainer}>
                  {/* Selector de Mes */}
                  <View style={styles.birthdayField}>
                    <Text style={styles.birthdayLabel}>Mes</Text>
                    <TouchableOpacity 
                      style={styles.birthdaySelector}
                      onPress={() => setShowMonthPicker(true)}
                    >
                      <Text style={formData.mes_cumple ? styles.birthdaySelectorText : styles.birthdayPlaceholder}>
                        {formData.mes_cumple ? meses.find(m => m.id === formData.mes_cumple)?.nombre : 'Selecciona'}
                      </Text>
                      <Text style={styles.arrow}>▼</Text>
                    </TouchableOpacity>
                  </View>
                  
                  {/* Selector de Día */}
                  <View style={styles.birthdayField}>
                    <Text style={styles.birthdayLabel}>Día</Text>
                    <TouchableOpacity 
                      style={[styles.birthdaySelector, !formData.mes_cumple && styles.birthdaySelectorDisabled]}
                      onPress={() => formData.mes_cumple && setShowDayPicker(true)}
                      disabled={!formData.mes_cumple}
                    >
                      <Text style={formData.dia_cumple ? styles.birthdaySelectorText : styles.birthdayPlaceholder}>
                        {formData.dia_cumple || (formData.mes_cumple ? 'Día' : 'Selecciona mes')}
                      </Text>
                      <Text style={styles.arrow}>▼</Text>
                    </TouchableOpacity>
                  </View>
                </View>
                {formData.mes_cumple && formData.dia_cumple && (
                  <Text style={styles.birthdayPreview}>
                    📅 {formData.dia_cumple} de {meses.find(m => m.id === formData.mes_cumple)?.nombre}
                  </Text>
                )}
              </View>
              
              {/* Modal Selector de Mes */}
              <Modal
                visible={showMonthPicker}
                animationType="slide"
                transparent={true}
                onRequestClose={() => setShowMonthPicker(false)}
              >
                <View style={styles.modalOverlay}>
                  <View style={styles.modalContent}>
                    <View style={styles.modalHeader}>
                      <Text style={styles.modalTitle}>Selecciona el Mes</Text>
                      <TouchableOpacity onPress={() => setShowMonthPicker(false)}>
                        <Text style={styles.closeButton}>✕</Text>
                      </TouchableOpacity>
                    </View>
                    <ScrollView>
                      {meses.map((mes) => (
                        <TouchableOpacity
                          key={mes.id}
                          style={[
                            styles.monthItem,
                            formData.mes_cumple === mes.id && styles.monthItemSelected
                          ]}
                          onPress={() => {
                            setFormData(prev => ({
                              ...prev,
                              mes_cumple: mes.id,
                              dia_cumple: undefined // Limpiar día al cambiar mes
                            }));
                            setShowMonthPicker(false);
                          }}
                        >
                          <Text style={[
                            styles.monthText,
                            formData.mes_cumple === mes.id && styles.monthTextSelected
                          ]}>
                            {mes.nombre}
                          </Text>
                          {formData.mes_cumple === mes.id && (
                            <Text style={styles.checkmark}>✓</Text>
                          )}
                        </TouchableOpacity>
                      ))}
                    </ScrollView>
                  </View>
                </View>
              </Modal>
              
              {/* Modal Selector de Día */}
              <Modal
                visible={showDayPicker}
                animationType="slide"
                transparent={true}
                onRequestClose={() => setShowDayPicker(false)}
              >
                <View style={styles.modalOverlay}>
                  <View style={styles.modalContent}>
                    <View style={styles.modalHeader}>
                      <Text style={styles.modalTitle}>Selecciona el Día</Text>
                      <TouchableOpacity onPress={() => setShowDayPicker(false)}>
                        <Text style={styles.closeButton}>✕</Text>
                      </TouchableOpacity>
                    </View>
                    <ScrollView contentContainerStyle={styles.daysGrid}>
                      {Array.from({ length: getDaysInMonth(formData.mes_cumple) }, (_, i) => i + 1).map((dia) => (
                        <TouchableOpacity
                          key={dia}
                          style={[
                            styles.dayItem,
                            formData.dia_cumple === dia && styles.dayItemSelected
                          ]}
                          onPress={() => {
                            setFormData(prev => ({ ...prev, dia_cumple: dia }));
                            setShowDayPicker(false);
                          }}
                        >
                          <Text style={[
                            styles.dayText,
                            formData.dia_cumple === dia && styles.dayTextSelected
                          ]}>
                            {dia}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </ScrollView>
                  </View>
                </View>
              </Modal>
              
              {/* Contraseña */}
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Contraseña *</Text>
                <TextInput
                  ref={passwordRef}
                  style={[styles.input, errors.password && styles.inputError]}
                  value={formData.password}
                  onChangeText={(value) => {
                    setFormData(prev => ({ ...prev, password: value }));
                    if (errors.password) {
                      setErrors(prev => ({ ...prev, password: false }));
                      setErrorMessages(prev => ({ ...prev, password: '' }));
                    }
                  }}
                  placeholder="Mínimo 6 caracteres"
                  secureTextEntry
                />
                {errorMessages.password && (
                  <Text style={styles.errorText}>{errorMessages.password}</Text>
                )}
              </View>
              
              {/* Confirmar Contraseña */}
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Confirmar contraseña *</Text>
                <TextInput
                  ref={passwordConfirmRef}
                  style={[styles.input, errors.password_confirmation && styles.inputError]}
                  value={formData.password_confirmation}
                  onChangeText={(value) => {
                    setFormData(prev => ({ ...prev, password_confirmation: value }));
                    if (errors.password_confirmation) {
                      setErrors(prev => ({ ...prev, password_confirmation: false }));
                      setErrorMessages(prev => ({ ...prev, password_confirmation: '' }));
                    }
                  }}
                  placeholder="Repite tu contraseña"
                  secureTextEntry
                />
                {errorMessages.password_confirmation && (
                  <Text style={styles.errorText}>{errorMessages.password_confirmation}</Text>
                )}
              </View>

              {/* Botón crear cuenta */}
              <TouchableOpacity
                onPress={handleRegister}
                disabled={isLoading || checkingEmail || checkingPhone}
                style={[
                  styles.createButton,
                  (isLoading || checkingEmail || checkingPhone) && styles.createButtonDisabled
                ]}
                activeOpacity={0.8}
              >
                <Text style={styles.createButtonText}>
                  {checkingEmail || checkingPhone ? '🔍 VERIFICANDO...' : isLoading ? '⏳ CREANDO CUENTA...' : '🚀 CREAR MI CUENTA'}
                </Text>
              </TouchableOpacity>

              {/* Indicador de éxito */}
              <View style={styles.successIndicator}>
                <Text style={styles.successText}>✅ Scroll funcionando correctamente</Text>
                <Text style={styles.successSubtext}>Si ves este mensaje, llegaste al final del formulario</Text>
              </View>
            </View>
          </ScrollView>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#E74C3C',
  },
  welcomeContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 40,
  },
  welcomeTitle: {
    fontSize: 32,
    fontWeight: 'bold',
    color: 'white',
    textAlign: 'center',
    marginBottom: 10,
  },
  welcomeSubtitle: {
    fontSize: 18,
    color: 'white',
    textAlign: 'center',
    marginBottom: 40,
    opacity: 0.9,
  },
  openModalButton: {
    backgroundColor: 'white',
    paddingHorizontal: 40,
    paddingVertical: 20,
    borderRadius: 25,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  openModalButtonText: {
    color: '#E74C3C',
    fontSize: 18,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  loginLink: {
    paddingVertical: 15,
  },
  loginLinkText: {
    color: 'white',
    fontSize: 16,
    textAlign: 'center',
    textDecorationLine: 'underline',
  },
  modalContainer: {
    flex: 1,
    backgroundColor: '#F8F9FA',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 15,
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5E5',
  },
  closeButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeButtonText: {
    fontSize: 24,
    color: '#666',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#2C3E50',
  },
  placeholder: {
    width: 40,
  },
  modalScrollView: {
    flex: 1,
  },
  formContainer: {
    paddingHorizontal: 20,
    paddingVertical: 20,
  },
  inputGroup: {
    marginBottom: 20,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: '#34495E',
    marginBottom: 8,
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderWidth: 2,
    borderColor: '#E5E5E5',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 14,
    fontSize: 16,
    color: '#2C3E50',
  },
  inputError: {
    borderColor: '#E74C3C',
    borderWidth: 2,
    backgroundColor: '#FFF5F5',
  },
  errorText: {
    color: '#E74C3C',
    fontSize: 13,
    marginTop: 6,
    marginLeft: 4,
    fontWeight: '500',
  },
  infoText: {
    color: '#3498DB',
    fontSize: 13,
    marginTop: 6,
    marginLeft: 4,
    fontStyle: 'italic',
  },
  birthdayContainer: {
    flexDirection: 'row',
    gap: 12,
  },
  birthdayField: {
    flex: 1,
  },
  birthdayLabel: {
    fontSize: 14,
    color: '#7F8C8D',
    marginBottom: 6,
    fontWeight: '500',
  },
  birthdaySelector: {
    backgroundColor: '#FFFFFF',
    borderWidth: 2,
    borderColor: '#E5E5E5',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 14,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  birthdaySelectorDisabled: {
    backgroundColor: '#F8F9FA',
    opacity: 0.6,
  },
  birthdaySelectorText: {
    fontSize: 16,
    color: '#2C3E50',
  },
  birthdayPlaceholder: {
    fontSize: 16,
    color: '#95A5A6',
  },
  birthdayPreview: {
    marginTop: 8,
    fontSize: 14,
    color: '#E74C3C',
    fontWeight: '500',
  },
  arrow: {
    fontSize: 12,
    color: '#95A5A6',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '70%',
    paddingBottom: 20,
  },
  monthItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F8F9FA',
  },
  monthItemSelected: {
    backgroundColor: '#FFF5F5',
  },
  monthText: {
    fontSize: 16,
    color: '#2C3E50',
  },
  monthTextSelected: {
    fontWeight: 'bold',
    color: '#E74C3C',
  },
  checkmark: {
    fontSize: 20,
    color: '#E74C3C',
    fontWeight: 'bold',
  },
  daysGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    padding: 16,
    justifyContent: 'space-between',
  },
  dayItem: {
    width: '13%',
    aspectRatio: 1,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
    backgroundColor: '#F8F9FA',
    marginBottom: 8,
  },
  dayItemSelected: {
    backgroundColor: '#E74C3C',
  },
  dayText: {
    fontSize: 16,
    color: '#2C3E50',
  },
  dayTextSelected: {
    color: '#FFFFFF',
    fontWeight: 'bold',
  },
  birthdayInput: {
    backgroundColor: '#FFFFFF',
    borderWidth: 2,
    borderColor: '#E5E5E5',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 14,
    fontSize: 16,
    color: '#2C3E50',
    textAlign: 'center',
  },
  createButton: {
    backgroundColor: '#E74C3C',
    paddingVertical: 18,
    paddingHorizontal: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 20,
    shadowColor: '#E74C3C',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 6,
    minHeight: 60,
  },
  createButtonDisabled: {
    backgroundColor: '#BDC3C7',
    shadowOpacity: 0,
    elevation: 0,
  },
  createButtonText: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: 'bold',
    textAlign: 'center',
    letterSpacing: 0.5,
  },
  successIndicator: {
    backgroundColor: '#2ECC71',
    padding: 20,
    borderRadius: 10,
    marginTop: 30,
    marginBottom: 40,
    alignItems: 'center',
  },
  successText: {
    color: 'white',
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 5,
  },
  successSubtext: {
    color: 'white',
    fontSize: 14,
    textAlign: 'center',
  },
});

export default RegisterScreen;
